import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ResolvedUrl } from '@wearejh/m2-pwa-engine/lib/utils/resolve';

import { StoreState } from 'src/types/global-types';

import { NavigationCompleteData } from './types';

/**
 * Callback function executed when navigation completes or on initial resolved load.
 * @param data - Information about the completed route.
 */
type OnNavigationComplete = (data: NavigationCompleteData) => void;

/**
 * Selects router state needed by the hook.
 * Encapsulated for clarity and potential reuse/testing.
 */
function useRouterState() {
    return useSelector((state: StoreState) => {
        const isResolving = state.router.resolving;
        const pathname = state.router.location.pathname;
        const urlKey = state.router.current?.urlKey;
        const isClientRoutingComplete = !isResolving && urlKey === pathname;

        return {
            isClientRoutingComplete,
            componentName: state.router.current?.componentName ?? undefined,
            id: state.router.current?.id ?? undefined,
            isResolving,
            pathname,
        };
    });
}

export type UseNavigationCompleteProps = {
    onComplete?: OnNavigationComplete;
    serverResolvedUrl?: ResolvedUrl;
};
/**
 * Detect when router navigation has completed.
 * It monitors the router's resolving state and calls a callback
 * both after client-side navigation finishes and on the initial
 * page load if the route is already resolved.
 *
 * @param onComplete Callback function executed upon completion.
 * @example
 * const handleNavComplete = useCallback((data) => {
 *   console.log('Navigation completed:', data);
 * }, []);
 *
 * useNavigationComplete(handleNavComplete);
 */
export function useNavigationComplete(props?: UseNavigationCompleteProps) {
    const { onComplete, serverResolvedUrl } = props || {};

    const { componentName, id, isClientRoutingComplete, isResolving, pathname } = useRouterState();

    // Track the previous resolving state to detect transitions
    const previousUrlRef = useRef<string | undefined>(undefined);

    // Track if this is the very first execution of the effect
    const isFirstRunRef = useRef<boolean>(true);

    useEffect(() => {
        const previousUrl = previousUrlRef.current;
        const hasUrlChanged = previousUrl !== pathname;

        if (!hasUrlChanged) {
            return;
        }

        const isFirstRun = isFirstRunRef.current;
        const isInitialLoadComplete = isFirstRun && !isResolving;

        const isRoutingComplete = isInitialLoadComplete || isClientRoutingComplete;

        if (!isRoutingComplete) {
            return;
        }

        isFirstRunRef.current = false;
        previousUrlRef.current = pathname;

        if (componentName === undefined && id === undefined && pathname === serverResolvedUrl?.urlKey) {
            return onComplete?.({
                componentName: serverResolvedUrl?.componentName,
                id: serverResolvedUrl?.id,
                pathname,
            });
        }

        onComplete?.({
            componentName,
            id,
            pathname,
        });
    }, [
        componentName,
        id,
        isClientRoutingComplete,
        isResolving,
        onComplete,
        pathname,
        serverResolvedUrl?.componentName,
        serverResolvedUrl?.id,
        serverResolvedUrl?.urlKey,
    ]);
}
